import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { setStatus } from 'store/Admin/OrderDisplay';
import { setOrderId, setOrderType } from 'store/Admin/Analyser';

import { ORDERSTATUS } from 'utils/Constants';
import { useWebsiteFilter } from 'utils/hooks/useWebsiteFilter';

import ProjectsPageHeader from 'components/shared/PMProjectsPageHeader';
import ProjectTable from 'components/shared/ProjectTable';

import 'pages/AdminClient/order.css';
import { adminClientOrderListSelector } from 'store/AdminClient/OrderList';
import {
  fetchadminCLientOrderDisplay,
  fetchadminCLientOrderLineDisplay,
  fetchAdminClientTextOrderDisplay,
} from 'store/AdminClient/OrderDisplay/features';
import usePageTracking from 'utils/hooks/usePageTracking';

const PROJECT_MANAGER_PROJECTS_DELAYED_PAGE = '/ProjectManager/ProjectsEnRetard';

const AC_FILTER_ROUTES = {
  ALL: '/ProjectManager/ProjectsEnCours',
  [ORDERSTATUS.NONATTRIBUTED]: '/ProjectManager/ProjectsEnCours',
  [ORDERSTATUS.INPROGRESS]: '/ProjectManager/ProjectsEnCours',
  [ORDERSTATUS.DELAYED]: '/ProjectManager/ProjectsEnRetard',
  [ORDERSTATUS.VALID]: '/ProjectManager/ProjectsValides',
};

export default function DelayedProjects() {
  usePageTracking(PROJECT_MANAGER_PROJECTS_DELAYED_PAGE);

  const dispatch = useDispatch();
  const { t } = useTranslation();

  const { delayedOrders = [], isLoading, isError } = useSelector(adminClientOrderListSelector);

  const [searchValue, setSearchValue] = useState('');
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [selectedWebsiteUrl] = useWebsiteFilter('AC_Dashboard_SelectedWebsite');

  useEffect(() => {
    window.dispatchEvent(new Event('locationchange'));
  }, []);

  const normalizeUrl = (url) => {
    if (!url) return '';
    return url.toString().trim().toLowerCase()
      .replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/+$/, '');
  };

  const filteredOrders = useMemo(() => {
    const q = (searchValue || '').toString().trim().toLowerCase();

    const matchesSearch = (order) => {
      if (!q) return true;
      const haystack = [
        order?.title, order?.keyword, order?.url,
        order?.urlPage, order?.websiteUrl, order?.website?.urlSite,
      ].filter(Boolean).map((v) => v.toString().toLowerCase()).join(' ');
      return haystack.includes(q);
    };

    if (!selectedWebsiteUrl) return delayedOrders.filter(matchesSearch);

    const normalizedSelectedUrl = normalizeUrl(selectedWebsiteUrl);
    return delayedOrders
      .filter((order) => {
        const orderWebsiteUrl = order.websiteUrl || order.website?.urlSite || '';
        return normalizeUrl(orderWebsiteUrl) === normalizedSelectedUrl;
      })
      .filter(matchesSearch);
  }, [delayedOrders, searchValue, selectedWebsiteUrl]);

  return (
    <div>
      <ProjectsPageHeader
        activeFilter={ORDERSTATUS.DELAYED}
        filterRoutes={AC_FILTER_ROUTES}
        addButtonPath="/ProjectManager/CreateProject"
      />

      {/* Search bar */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 12, marginBottom: 12 }}>
        {!isSearchOpen ? (
          <img
            src="/Images/search-icon.png"
            alt="search"
            onClick={() => setIsSearchOpen(true)}
            style={{ width: 32, height: 32, cursor: 'pointer' }}
          />
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, maxWidth: 320, width: '100%' }}>
            <div style={{ position: 'relative', flex: 1, display: 'flex', alignItems: 'center' }}>
              <img
                src="/Images/search-icon2.png"
                alt="search"
                style={{ position: 'absolute', left: 10, width: 16, height: 16, pointerEvents: 'none' }}
              />
              <input
                type="text"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                placeholder={t('Rechercher')}
                style={{ width: '100%', padding: '8px 36px 8px 34px', border: '1px solid #E5E7EB', borderRadius: 6, fontSize: 14, outline: 'none' }}
              />
            </div>
            <button
              type="button"
              onClick={() => { setIsSearchOpen(false); setSearchValue(''); }}
              style={{ background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 6, padding: '6px 10px', cursor: 'pointer', fontSize: 13, color: '#111827' }}
            >
              {t('Fermer')}
            </button>
          </div>
        )}
      </div>

      <ProjectTable
        orderStatus={ORDERSTATUS.DELAYED}
        orders={filteredOrders}
        isLoading={isLoading}
        isError={isError}
        fetchDisplayData={(id, orderType) => {
          // ← Fix issue 2
          window.localStorage.setItem('ProjectManager_ProjectDisplay_id', id);
          dispatch(setStatus(ORDERSTATUS.DELAYED));
          dispatch(setOrderId(id));
          dispatch(setOrderType(orderType));
          dispatch(fetchadminCLientOrderDisplay(id));
          dispatch(fetchAdminClientTextOrderDisplay(id));
          dispatch(fetchadminCLientOrderLineDisplay(id));
        }}
        onRowClickRedirectPath="/ProjectManager/DetailsProject"
        hideTitle={true}
        hideSearch={true}
        disableWebsiteFilter={true}
        hidePaginationSelector={true}
      />
    </div>
  );
}